import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import shippingGroupsEntriesSelector from 'selectors/order/orderDetails/shippingGroups/shippingGroupsEntries/shippingGroupsEntriesSelector';
import { orderDetailsSelector } from 'selectors/order/orderDetails/orderDetailsSelector';
import LanguageLocale from 'utils/LanguageLocale';
import OrderUtils from 'utils/Order';
import userUtils from 'utils/User';
import store from 'store/Store';
import OrderActions from 'actions/OrderActions';
import checkoutApi from 'services/api/checkout';
import orderShippingMethodsSelector from 'selectors/order/orderShippingMethods/orderShippingMethodsSelector';
import Empty from 'constants/empty';
import CheckoutUtils from 'utils/Checkout';
import Actions from 'Actions';
import DeliveryOptionsUtils from 'utils/DeliveryOptions';

import DeliveryWindow from 'components/FrictionlessCheckout/SddSection/DeliveryWindow/DeliveryWindow';
import SddSectionEN from 'components/FrictionlessCheckout/SddSection/locales/SddSection_en_US';
import SddSectionFR from 'components/FrictionlessCheckout/SddSection/locales/SddSection_fr_CA';
import DeliveryWindowOptionEN from 'components/FrictionlessCheckout/SddSection/DeliveryWindow/locales/DeliveryWindowOption_en_US';
import DeliveryWindowOptionFR from 'components/FrictionlessCheckout/SddSection/DeliveryWindow/locales/DeliveryWindowOption_fr_CA';

const { dispatch } = store;
const { updateOrder, showScheduledDeliveryUnavailable } = OrderActions;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocale;
const getText = getLocalisedDataFromResource(SddSectionEN, SddSectionFR);
const getDeliveryText = getLocalisedDataFromResource(DeliveryWindowOptionEN, DeliveryWindowOptionFR);

const onDeliveryWindowSaved = () => {
    dispatch(showScheduledDeliveryUnavailable(false));
    checkoutApi.getOrderDetails(OrderUtils.getOrderId()).then(newOrderDetails => {
        dispatch(updateOrder(newOrderDetails));
    });
};

const localization = createStructuredSelector({
    free: getTextFromResource(getDeliveryText, 'free'),
    freeForSDU: getTextFromResource(getDeliveryText, 'freeForSDU'),
    ok: getTextFromResource(getDeliveryText, 'ok'),
    errorTitle: getTextFromResource(getDeliveryText, 'errorTitle')
});

const resolveDeliveryMethods = shippingMethods => {
    const isNDDEnabled = DeliveryOptionsUtils.isNDDEnabled();

    if (isNDDEnabled) {
        const nddDeliveryMethod = shippingMethods?.find(m => m.shippingMethodType?.includes(getText('nextDay'))) || Empty.Object;

        return {
            sameDayDeliveryMethod: shippingMethods?.find(m => m.shippingMethodType?.includes(getText('sameDay'))) || Empty.Object,
            nddDeliveryMethod,
            scheduledSameDayDeliveryMethod: shippingMethods?.find(m => m.shippingMethodType?.includes(getText('scheduled'))) || Empty.Object
        };
    }

    return {
        sameDayDeliveryMethod: shippingMethods ? shippingMethods[0] : Empty.Object,
        scheduledSameDayDeliveryMethod: shippingMethods ? shippingMethods[1] : Empty.Object,
        nddDeliveryMethod: Empty.Object
    };
};

const withDeliveryWindowProps = connect(
    createSelector(
        orderDetailsSelector,
        shippingGroupsEntriesSelector,
        orderShippingMethodsSelector,
        localization,
        (orderDetails, shippingGroups, orderShippingMethods, textResources) => {
            const { isScheduledSDDDeliveryEnabled } = orderDetails?.header;
            const sameDayShippingGroup =
                shippingGroups.find(group => group.shippingGroupType === OrderUtils.SHIPPING_GROUPS.SAME_DAY)?.shippingGroup || {};
            const isDeliveryWindowScheduled =
                sameDayShippingGroup.shippingMethod?.shippingMethodType?.includes('Schedule') ||
                sameDayShippingGroup.shippingMethod?.shippingMethodType?.includes('Planifier');
            const sameDayShippingGroupId = sameDayShippingGroup.shippingGroupId;
            const selectedShippingMethodId = sameDayShippingGroup.shippingMethod?.shippingMethodId;
            let shippingMethods;

            if (sameDayShippingGroup.availableShippingMethods) {
                shippingMethods = sameDayShippingGroup.availableShippingMethods;
            } else {
                for (const property in orderShippingMethods) {
                    if (orderShippingMethods[property]) {
                        const methods = orderShippingMethods[property];
                        const foundSameDayDelivery = OrderUtils.getSameDayShippingMethod(methods);

                        if (foundSameDayDelivery) {
                            shippingMethods = orderShippingMethods[property];
                        }
                    }
                }
            }

            const { sameDayDeliveryMethod, nddDeliveryMethod, scheduledSameDayDeliveryMethod } = resolveDeliveryMethods(shippingMethods);

            const sameDayDeliveryMethodId = sameDayDeliveryMethod?.shippingMethodId;
            const scheduledSDDShippingMethodId = scheduledSameDayDeliveryMethod?.shippingMethodId;
            const isSddAvailable = !!sameDayDeliveryMethodId;
            const { sameDayDeliveryLabel, sameDayDeliveryMessage } = sameDayDeliveryMethod;

            const nddShippingFee = nddDeliveryMethod.shippingFee;
            const nddDeliveryLabel = nddDeliveryMethod.sameDayDeliveryLabel;
            const nddDeliveryMessage = nddDeliveryMethod.sameDayDeliveryMessage;
            const nddDeliveryMethodId = nddDeliveryMethod?.shippingMethodId;
            const isNddAvailable = !!nddDeliveryMethodId;

            const deliveryWindowTitle = isDeliveryWindowScheduled
                ? `${scheduledSameDayDeliveryMethod.sameDayDeliveryMessage}, ${scheduledSameDayDeliveryMethod.sameDayDeliveryLabel}`
                : getText('deliveryWindowTitle');
            const deliveryWindowPrice = isScheduledSDDDeliveryEnabled && scheduledSameDayDeliveryMethod.shippingFee;
            const deliveryWindowMessage = !isScheduledSDDDeliveryEnabled
                ? getText('deliveryWindowUnavailable')
                : isDeliveryWindowScheduled
                    ? getText('chooseDifferentTime')
                    : '';

            const isSDDRougeFreeShipEligible = userUtils.isSDDRougeFreeShipEligible(); // kill-switch
            const isFreeRougeSameDayOrder = OrderUtils.isFreeRougeSameDayDelivery(sameDayDeliveryMethod);
            const isFreeRougeSDD = isSDDRougeFreeShipEligible && isFreeRougeSameDayOrder;
            const sameDayDeliveryBasket = OrderUtils.getSameDayDeliveryBasket(orderDetails);
            const isSDUInBasket = sameDayDeliveryBasket.items?.filter(item => item.sku.type === 'SDU').length > 0;
            const sameDayDeliveryPrice =
                isSDUInBasket && OrderUtils.isZeroPrice(sameDayDeliveryMethod.shippingFee) ? textResources.free : sameDayDeliveryMethod.shippingFee;
            const isFreeRougeSDDV2 = orderDetails.items.SDDRougeTestFreeShipping;

            return {
                sameDayDeliveryLabel,
                sameDayDeliveryMessage,
                sameDayDeliveryPrice,
                sameDayShippingGroupId,
                deliveryWindowTitle,
                deliveryWindowPrice,
                deliveryWindowMessage,
                isScheduledSDDDeliveryEnabled,
                scheduledSDDShippingMethodId,
                isDeliveryWindowScheduled,
                isFreeRougeSDD: isFreeRougeSDD || isFreeRougeSDDV2,
                sameDayDeliveryMethodId,
                nddShippingFee,
                nddDeliveryLabel,
                nddDeliveryMessage,
                nddDeliveryMethodId,
                isNddAvailable,
                isSddAvailable,
                selectedShippingMethodId,
                setShippingMethod: CheckoutUtils.setShippingMethod,
                localization: textResources
            };
        }
    ),
    { onDeliveryWindowSaved, showInfoModal: Actions.showInfoModal }
);

const ConnectedDeliveryWindow = withDeliveryWindowProps(DeliveryWindow);

export default ConnectedDeliveryWindow;
